/**
 * Organizations Management Page
 * Super Admin Only
 *
 * Lists all tenant organizations with usage against their message limits,
 * and manages subscription tier / status (which is what sets messageLimit)
 * plus billing-cycle usage resets.
 */

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import useCurrentUser from '../../hooks/useCurrentUser';

// Mirrors backend config.subscriptionTiers (backend/src/config/index.js).
// Changing a tier server-side resets the org's messageLimit to these values.
const TIER_OPTIONS = [
    { value: 'basic', label: 'Starter — 500 msgs/mo' },
    { value: 'pro', label: 'Pro — 2,500 msgs/mo' },
    { value: 'custom', label: 'Municipal & Enterprise — 10,000 msgs/mo' },
];

const STATUS_OPTIONS = ['active', 'trial', 'suspended', 'cancelled'];

const STATUS_BADGES = {
    active: 'bg-success',
    trial: 'bg-info',
    suspended: 'bg-warning text-dark',
    cancelled: 'bg-secondary',
};

function Organizations() {
    const { getAccessTokenSilently } = useAuth0();
    const { isSuperAdmin, loading: userLoading } = useCurrentUser();

    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    // Per-org pending edits: { [orgId]: { tier, status } }
    const [edits, setEdits] = useState({});
    const [savingOrgId, setSavingOrgId] = useState(null);

    useEffect(() => {
        if (isSuperAdmin) {
            loadOrganizations();
        }
    }, [isSuperAdmin]);

    const authHeaders = async () => {
        const token = await getAccessTokenSilently();
        return { Authorization: `Bearer ${token}` };
    };

    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const headers = await authHeaders();
            const res = await axios.get('/api/admin/organizations', { headers });
            setOrganizations(res.data.organizations);
            setEdits({});
        } catch (err) {
            console.error('Error loading organizations:', err);
            setMessage({ type: 'danger', text: 'Failed to load organizations.' });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (orgId, field, value) => {
        setEdits(prev => ({
            ...prev,
            [orgId]: { ...prev[orgId], [field]: value },
        }));
    };

    const applyChanges = async (org) => {
        const edit = edits[org.orgId];
        if (!edit) return;

        const payload = {};
        if (edit.tier && edit.tier !== org.subscriptionTier) payload.tier = edit.tier;
        if (edit.status && edit.status !== org.subscriptionStatus) payload.status = edit.status;

        if (Object.keys(payload).length === 0) return;

        if (payload.status === 'suspended' || payload.status === 'cancelled') {
            const ok = window.confirm(
                `Set "${org.name}" to ${payload.status}? The AI will stop answering messages for this organization.`
            );
            if (!ok) return;
        }

        try {
            setSavingOrgId(org.orgId);
            const headers = await authHeaders();
            await axios.post(`/api/admin/organizations/${org.orgId}/subscription`, payload, { headers });
            setMessage({ type: 'success', text: `Subscription updated for ${org.name}.` });
            setTimeout(() => setMessage(null), 3000);
            await loadOrganizations();
        } catch (err) {
            console.error('Error updating subscription:', err);
            setMessage({ type: 'danger', text: `Failed to update subscription for ${org.name}.` });
        } finally {
            setSavingOrgId(null);
        }
    };

    const resetUsage = async (org) => {
        const ok = window.confirm(
            `Reset usage for "${org.name}"? Message count returns to 0 and a new 30-day billing cycle starts today.`
        );
        if (!ok) return;

        try {
            setSavingOrgId(org.orgId);
            const headers = await authHeaders();
            await axios.post(`/api/admin/organizations/${org.orgId}/reset-usage`, {}, { headers });
            setMessage({ type: 'success', text: `Usage reset for ${org.name}.` });
            setTimeout(() => setMessage(null), 3000);
            await loadOrganizations();
        } catch (err) {
            console.error('Error resetting usage:', err);
            setMessage({ type: 'danger', text: `Failed to reset usage for ${org.name}.` });
        } finally {
            setSavingOrgId(null);
        }
    };

    const hasPendingChanges = (org) => {
        const edit = edits[org.orgId];
        if (!edit) return false;
        return (edit.tier && edit.tier !== org.subscriptionTier)
            || (edit.status && edit.status !== org.subscriptionStatus);
    };

    const usageBarClass = (pct) => {
        if (pct >= 100) return 'bg-danger';
        if (pct >= 80) return 'bg-warning';
        return 'bg-success';
    };

    if (userLoading) return <div className="p-5 text-center">Loading...</div>;
    if (!isSuperAdmin) return <div className="p-5 text-center">Access Denied</div>;
    if (loading) return <div className="p-5 text-center">Loading Organizations...</div>;

    return (
        <div className="container-fluid py-4">
            <h2 className="mb-4">Organizations</h2>

            {message && (
                <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                </div>
            )}

            <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="mb-0">Subscription & Usage Limits</h5>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Organization</th>
                                <th>Tier</th>
                                <th>Status</th>
                                <th style={{ minWidth: '180px' }}>Usage</th>
                                <th>Cycle Ends</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {organizations.map(org => {
                                const edit = edits[org.orgId] || {};
                                const pct = Math.min(org.usagePercentage || 0, 100);
                                return (
                                    <tr key={org.orgId}>
                                        <td className="ps-4">
                                            <div className="fw-bold">{org.name}</div>
                                            <div className="text-muted small font-monospace">{org.orgId}</div>
                                        </td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ maxWidth: '280px' }}
                                                value={edit.tier ?? org.subscriptionTier}
                                                onChange={(e) => handleEdit(org.orgId, 'tier', e.target.value)}
                                            >
                                                {TIER_OPTIONS.map(t => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ maxWidth: '140px' }}
                                                value={edit.status ?? org.subscriptionStatus}
                                                onChange={(e) => handleEdit(org.orgId, 'status', e.target.value)}
                                            >
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <span className={`badge mt-1 ${STATUS_BADGES[org.subscriptionStatus] || 'bg-secondary'}`}>
                                                {org.subscriptionStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="small mb-1">
                                                {org.messageCount} / {org.messageLimit} messages
                                                {org.isOverLimit && (
                                                    <span className="badge bg-danger ms-2">Over limit</span>
                                                )}
                                            </div>
                                            <div className="progress" style={{ height: '6px' }}>
                                                <div
                                                    className={`progress-bar ${usageBarClass(org.usagePercentage || 0)}`}
                                                    role="progressbar"
                                                    style={{ width: `${pct}%` }}
                                                    aria-valuenow={pct}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                ></div>
                                            </div>
                                        </td>
                                        <td className="text-muted small">
                                            {org.billingCycleEnd ? new Date(org.billingCycleEnd).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="text-end pe-4">
                                            <button
                                                className="btn btn-sm btn-primary me-2"
                                                disabled={!hasPendingChanges(org) || savingOrgId === org.orgId}
                                                onClick={() => applyChanges(org)}
                                            >
                                                {savingOrgId === org.orgId ? 'Saving...' : 'Apply'}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={savingOrgId === org.orgId}
                                                onClick={() => resetUsage(org)}
                                            >
                                                Reset Usage
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {organizations.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">No organizations found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="card-footer bg-white text-muted small">
                    Changing tier resets the organization&apos;s message limit to the tier default.
                    &quot;Reset Usage&quot; zeroes the message count and starts a new 30-day billing cycle.
                </div>
            </div>
        </div>
    );
}

export default Organizations;
